import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axiosInstance";
import {
  getStockEndpoint,
  getTransactionEndpoint,
  TRANSACTION_LABELS,
} from "./stockQrUtils";
import { WarehouseRackPicker } from "./ChipPickers";
import "./StockQR.scss";

// All warehouse-stock endpoints live under /api/inv-manager, so always use this base URL
const INV_MANAGER_URL = process.env.REACT_APP_API_INV_MANAGER_URL;

const naturalSort = (a, b) =>
  String(a).localeCompare(String(b), undefined, { numeric: true });

const variantName = (c, fallback) =>
  c.combinationDisplayName || c.combinationSku || fallback || "Default variant";

/* ---------- stock overview: each warehouse once, with all its racks ---------- */

const StockByWarehouse = ({ stocks }) => {
  if (!stocks?.length)
    return <p className="scan-empty">No warehouse stock recorded yet.</p>;

  const byWarehouse = {};
  stocks.forEach((st) => {
    const key = String(st.warehouseId);
    if (!byWarehouse[key])
      byWarehouse[key] = {
        name: st.warehouseName || `Warehouse ${key}`,
        unit: st.unit,
        rows: [],
        total: 0,
      };
    byWarehouse[key].rows.push(st);
    byWarehouse[key].total += Number(st.quantity) || 0;
  });

  return (
    <div className="wh-overview">
      {Object.entries(byWarehouse).map(([key, wh]) => (
        <div key={key} className="wh-card">
          <div className="wh-card-head">
            <strong>{wh.name}</strong>
            <span>
              {wh.total} <small>{wh.unit}</small>
            </span>
          </div>
          {[...wh.rows]
            .sort((a, b) => naturalSort(a.rackNumber || "", b.rackNumber || ""))
            .map((st) => (
              <div key={st.id} className="wh-rack-row">
                <span className="rack-chip">
                  📍 {st.rackNumber || "No rack"}
                </span>
                <b>
                  {st.quantity} <small>{st.unit}</small>
                </b>
              </div>
            ))}
        </div>
      ))}
    </div>
  );
};

/* ---------- stock update (Inbound / Outbound / Transfer) ---------- */

const TransactionPanel = ({
  combo,
  type,
  materialId,
  warehouseList,
  rackMap,
  onDone,
}) => {
  const stocks = combo.warehouseStocks || [];
  const hasStock = stocks.length > 0;

  const [mode, setMode] = useState("INBOUND");
  const [warehouseId, setWarehouseId] = useState("");
  const [rack, setRack] = useState("");
  const [qty, setQty] = useState("");
  const [remarks, setRemarks] = useState("");
  const [recordId, setRecordId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [toRack, setToRack] = useState("");
  const [saving, setSaving] = useState(false);

  const record = stocks.find((s) => String(s.id) === String(recordId));
  const cap = mode === "INBOUND" ? null : record ? Number(record.quantity) : 0;
  const qtyNum = Number(qty) || 0;

  const switchMode = (m) => {
    if ((m === "OUTBOUND" || m === "TRANSFER") && !hasStock) return;
    setMode(m);
    setQty("");
    setRecordId("");
    setWarehouseId("");
    setRack("");
    setToWarehouseId("");
    setToRack("");
  };

  const clamp = (v) => {
    let n = Math.max(0, v);
    if (cap !== null) n = Math.min(n, cap);
    return n;
  };
  const bump = (n) => setQty(String(clamp(qtyNum + n)));

  /* ----- warehouse cards (each warehouse once, racks inside) ----- */
  const usedIds = new Set(stocks.map((st) => String(st.warehouseId)));

  // Every warehouse + the racks known in it. Warehouses already holding this variant come first.
  const destinationGroups = warehouseList
    .map((w) => {
      const wid = String(w.id);
      const has = usedIds.has(wid);
      return {
        id: w.id,
        name: w.name,
        has,
        tag: has ? "" : "New",
        racks: (rackMap[wid] || []).map((r) => {
          const rec = stocks.find(
            (st) =>
              String(st.warehouseId) === wid && (st.rackNumber || "") === r,
          );
          return {
            key: `${wid}|${r}`,
            rack: r,
            label: r,
            sub: rec ? `${rec.quantity} ${rec.unit}` : undefined,
          };
        }),
      };
    })
    .sort(
      (a, b) => Number(b.has) - Number(a.has) || naturalSort(a.name, b.name),
    );

  // Existing stock records grouped by warehouse (Outbound / Transfer source)
  const sourceGroups = Object.values(
    stocks.reduce((acc, st) => {
      const wid = String(st.warehouseId);
      if (!acc[wid])
        acc[wid] = {
          id: st.warehouseId,
          name: st.warehouseName || `Warehouse ${wid}`,
          racks: [],
          sum: 0,
          unit: st.unit,
        };
      acc[wid].racks.push({
        key: String(st.id),
        label: st.rackNumber || "No rack",
        sub: `${st.quantity} ${st.unit}`,
      });
      acc[wid].sum += Number(st.quantity) || 0;
      return acc;
    }, {}),
  ).map((g) => ({ ...g, total: `${g.sum} ${g.unit}` }));

  const ready =
    mode === "INBOUND"
      ? !!warehouseId && !!rack.trim()
      : mode === "OUTBOUND"
        ? !!record
        : !!record && !!toWarehouseId && !!toRack.trim();

  const nameOf = (id) =>
    warehouseList.find((w) => String(w.id) === String(id))?.name ||
    `Warehouse ${id}`;

  const submit = async () => {
    const quantity = parseFloat(qty);
    if (!quantity || quantity <= 0)
      return toast.error("Enter a quantity greater than 0.");

    const common = {
      combinationId: combo.combinationId,
      productId:
        type === "product" ? materialId || stocks[0]?.productId || 0 : 0,
      rawMaterialId:
        type === "raw-material"
          ? materialId || stocks[0]?.rawMaterialId || 0
          : 0,
      quantity,
      remarks,
      transactionType: mode,
    };

    let item;
    if (mode === "INBOUND") {
      if (!warehouseId) return toast.error("Select a warehouse.");
      if (!rack.trim()) return toast.error("Select or enter a rack number.");
      item = {
        ...common,
        warehouseId: Number(warehouseId),
        rackNumber: rack.trim(),
      };
    } else if (mode === "OUTBOUND") {
      if (!record) return toast.error("Select the stock to take from.");
      if (quantity > record.quantity)
        return toast.error(`Only ${record.quantity} available in that rack.`);
      // Backend identifies the source by warehouse + rack (no warehouseStockHistoryId)
      item = {
        ...common,
        warehouseId: record.warehouseId,
        rackNumber: record.rackNumber || "",
      };
    } else {
      if (!record) return toast.error("Select the stock to move.");
      if (quantity > record.quantity)
        return toast.error(`Only ${record.quantity} available in that rack.`);
      if (!toWarehouseId)
        return toast.error("Select the destination warehouse.");
      if (!toRack.trim())
        return toast.error("Select or enter the destination rack.");
      if (
        Number(toWarehouseId) === record.warehouseId &&
        toRack.trim() === (record.rackNumber || "")
      )
        return toast.error("Destination is the same as the source.");
      item = {
        ...common,
        warehouseId: record.warehouseId,
        fromWarehouseId: record.warehouseId,
        toWarehouseId: Number(toWarehouseId),
        fromRackNumber: record.rackNumber || "",
        toRackNumber: toRack.trim(),
        warehouseStockHistoryId: record.id,
      };
    }

    try {
      setSaving(true);
      await axiosInstance.post(
        `${INV_MANAGER_URL}${getTransactionEndpoint(type)}`,
        [item],
      );
      toast.success(`${TRANSACTION_LABELS[mode]} saved`);
      setQty("");
      setRemarks("");
      onDone();
    } catch (e) {
      console.error("Stock transaction failed", e);
      toast.error("Could not save the transaction.");
    } finally {
      setSaving(false);
    }
  };

  const Step = ({ n, children }) => (
    <div className="scan-step">
      <span className="step-no">{n}</span>
      {children}
    </div>
  );

  const sourcePicker = (
    <WarehouseRackPicker
      groups={sourceGroups}
      selectedKey={String(recordId)}
      selectedWarehouseId={record ? record.warehouseId : ""}
      onSelect={(g, r) => {
        setRecordId(r.key);
        setQty("");
      }}
    />
  );

  return (
    <div className="scan-actions">
      <div className="scan-tabs" role="tablist">
        {["INBOUND", "OUTBOUND", "TRANSFER"].map((m) => {
          const disabled = m !== "INBOUND" && !hasStock;
          return (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              disabled={disabled}
              title={disabled ? "No existing stock for this variant" : ""}
              className={`scan-tab scan-tab--${m.toLowerCase()} ${mode === m ? "active" : ""}`}
              onClick={() => switchMode(m)}
            >
              {TRANSACTION_LABELS[m]}
            </button>
          );
        })}
      </div>

      {!hasStock && (
        <p className="scan-hint">
          No stock exists yet, so only <strong>Inbound / Add Stock</strong> is
          allowed. Outbound and Transfer unlock once stock is added.
        </p>
      )}

      {mode === "INBOUND" && (
        <>
          <Step n="1">Choose warehouse and rack</Step>
          <WarehouseRackPicker
            groups={destinationGroups}
            selectedWarehouseId={warehouseId}
            selectedKey={warehouseId && rack ? `${warehouseId}|${rack}` : ""}
            customValue={rack}
            allowNew
            onSelect={(g, r) => {
              setWarehouseId(String(g.id));
              setRack(r.rack);
            }}
            onNew={(g, text) => {
              setWarehouseId(String(g.id));
              setRack(text);
            }}
            emptyText="No warehouses found."
          />
          {warehouseId && rack.trim() && (
            <p className="scan-pick">
              Adding to <strong>{nameOf(warehouseId)}</strong> · 📍{" "}
              <strong>{rack.trim()}</strong>
            </p>
          )}
        </>
      )}

      {mode === "OUTBOUND" && (
        <>
          <Step n="1">Take stock from</Step>
          {sourcePicker}
        </>
      )}

      {mode === "TRANSFER" && (
        <>
          <Step n="1">Move stock from</Step>
          {sourcePicker}
          {record && (
            <>
              <Step n="2">Move to warehouse and rack</Step>
              <WarehouseRackPicker
                groups={destinationGroups}
                selectedWarehouseId={toWarehouseId}
                selectedKey={
                  toWarehouseId && toRack ? `${toWarehouseId}|${toRack}` : ""
                }
                customValue={toRack}
                allowNew
                onSelect={(g, r) => {
                  setToWarehouseId(String(g.id));
                  setToRack(r.rack);
                }}
                onNew={(g, text) => {
                  setToWarehouseId(String(g.id));
                  setToRack(text);
                }}
              />
              {toWarehouseId && toRack.trim() && (
                <p className="scan-pick">
                  Moving to <strong>{nameOf(toWarehouseId)}</strong> · 📍{" "}
                  <strong>{toRack.trim()}</strong>
                </p>
              )}
            </>
          )}
        </>
      )}

      {ready && (
        <>
          <Step n={mode === "TRANSFER" ? "3" : "2"}>
            {mode === "OUTBOUND"
              ? "Quantity to remove"
              : mode === "TRANSFER"
                ? "Quantity to move"
                : "Quantity to add"}
            {record && mode !== "INBOUND"
              ? ` (max ${record.quantity} ${record.unit})`
              : ""}
          </Step>
          <div className="scan-qty-row">
            {mode === "INBOUND" && (
              <>
                <button
                  type="button"
                  className="quick-btn quick-btn--add"
                  onClick={() => bump(1)}
                >
                  +1
                </button>
                <button
                  type="button"
                  className="quick-btn quick-btn--add"
                  onClick={() => bump(10)}
                >
                  +10
                </button>
                <button
                  type="button"
                  className="quick-btn quick-btn--add"
                  onClick={() => bump(100)}
                >
                  +100
                </button>
              </>
            )}
            {mode === "OUTBOUND" && (
              <>
                <button
                  type="button"
                  className="quick-btn quick-btn--remove"
                  onClick={() => bump(1)}
                >
                  −1
                </button>
                <button
                  type="button"
                  className="quick-btn quick-btn--remove"
                  onClick={() => bump(10)}
                >
                  −10
                </button>
                <button
                  type="button"
                  className="quick-btn quick-btn--remove"
                  onClick={() => setQty(String(cap))}
                >
                  All
                </button>
              </>
            )}
            {mode === "TRANSFER" && (
              <button
                type="button"
                className="quick-btn quick-btn--neutral"
                onClick={() => setQty(String(cap))}
              >
                All
              </button>
            )}
            <input
              type="number"
              min="0"
              placeholder="Qty"
              value={qty}
              onChange={(e) =>
                setQty(
                  e.target.value === ""
                    ? ""
                    : String(clamp(Number(e.target.value))),
                )
              }
            />
          </div>
          {record && qtyNum > 0 && mode !== "INBOUND" && (
            <small className="scan-after">
              {record.quantity} → {record.quantity - qtyNum} left in this rack
            </small>
          )}

          <input
            className="scan-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Remarks (optional)"
          />

          <button
            type="button"
            className="scan-submit"
            onClick={submit}
            disabled={saving || qtyNum <= 0}
          >
            {saving ? "Saving…" : `Update · ${TRANSACTION_LABELS[mode]}`}
          </button>
        </>
      )}
    </div>
  );
};

/* ---------- selected variant: stock overview + stock update side by side ---------- */

const VariantDetail = ({
  combo,
  type,
  materialId,
  warehouseList,
  rackMap,
  fallbackName,
  onDone,
}) => (
  <>
    <div className="scan-detail-title">
      <div>
        <h4>{variantName(combo, fallbackName)}</h4>
        {combo.combinationSku && combo.combinationDisplayName && (
          <small>SKU: {combo.combinationSku}</small>
        )}
      </div>
      <span className="pill">Total {combo.totalStock}</span>
    </div>
    <div className="scan-detail-grid">
      <div>
        <h5 className="scan-section-title">Stock by warehouse</h5>
        <StockByWarehouse stocks={combo.warehouseStocks} />
      </div>
      <div>
        <h5 className="scan-section-title">Update stock</h5>
        <TransactionPanel
          key={combo.combinationId}
          combo={combo}
          type={type}
          materialId={materialId}
          warehouseList={warehouseList}
          rackMap={rackMap}
          onDone={onDone}
        />
      </div>
    </div>
  </>
);

/* ---------- main popup (full screen): Product + variants + stock update ---------- */

/**
 * params: output of parseScanParams()
 * onClose: called when the popup is dismissed
 * onChanged: optional, called after a transaction was saved
 */
const StockScanPopup = ({ params, onClose, onChanged }) => {
  const { type, materialId, materialName, combinationName, combinationIds } =
    params;

  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const results = await Promise.allSettled(
        combinationIds.map((id) =>
          axiosInstance.get(`${INV_MANAGER_URL}${getStockEndpoint(type, id)}`),
        ),
      );
      const ok = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value.data?.data)
        .filter(Boolean);
      if (!ok.length)
        setError("Could not load stock details for this QR code.");
      setCombos(ok);
    } catch (e) {
      console.error("Scan popup load failed", e);
      setError("Could not load stock details for this QR code.");
    } finally {
      setLoading(false);
    }
  }, [combinationIds, type]);

  useEffect(() => {
    load();
  }, [load]);

  // Esc closes the full-screen popup
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Keep a valid variant selected (first one by default, survives reloads)
  useEffect(() => {
    if (!combos.length) return;
    if (!combos.some((c) => c.combinationId === selectedId))
      setSelectedId(combos[0].combinationId);
  }, [combos, selectedId]);

  const displayName = useMemo(() => {
    const rec = combos.flatMap((c) => c.warehouseStocks || [])[0];
    const fromApi =
      type === "raw-material" ? rec?.rawMaterialName : rec?.productName;
    return fromApi || materialName || "Item";
  }, [combos, type, materialName]);

  const grandTotal = combos.reduce((sum, c) => sum + (c.totalStock || 0), 0);

  // Every warehouse once, with a name. Uses the API's warehouse list plus any warehouse that already holds stock.
  const warehouseList = useMemo(() => {
    const map = new Map();
    combos.forEach((c) => {
      (c.warehouses || []).forEach((w) => {
        if (/^(DISABLED|INACTIVE)$/i.test(w.status || "")) return;
        map.set(String(w.id), {
          id: w.id,
          name:
            w.name || w.warehouseName || w.warehouseCode || `Warehouse ${w.id}`,
        });
      });
      (c.warehouseStocks || []).forEach((st) => {
        if (!map.has(String(st.warehouseId)))
          map.set(String(st.warehouseId), {
            id: st.warehouseId,
            name: st.warehouseName || `Warehouse ${st.warehouseId}`,
          });
      });
    });
    return [...map.values()];
  }, [combos]);

  // Racks already used in each warehouse (across all loaded variants)
  const rackMap = useMemo(() => {
    const map = {};
    combos.forEach((c) =>
      (c.warehouseStocks || []).forEach((st) => {
        if (!st.rackNumber) return;
        const key = String(st.warehouseId);
        map[key] = [...new Set([...(map[key] || []), st.rackNumber])].sort(
          naturalSort,
        );
      }),
    );
    return map;
  }, [combos]);

  const handleDone = async () => {
    await load();
    if (onChanged) onChanged();
  };

  const filtered = combos.filter((c) =>
    `${c.combinationDisplayName || ""} ${c.combinationSku || ""}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const selected = combos.find((c) => c.combinationId === selectedId);
  const multi = combos.length > 1;

  return (
    <div
      className="scan-overlay scan-overlay--full"
      role="dialog"
      aria-modal="true"
    >
      <div className="scan-popup scan-popup--full">
        <header className="scan-header">
          <div>
            <span className="scan-kind">
              {type === "raw-material" ? "Raw material" : "Product"}
            </span>
            <h3>{displayName}</h3>
            {!loading && !error && (
              <p className="scan-subline">
                {combos.length} variant{combos.length !== 1 ? "s" : ""} · total
                stock <strong>{grandTotal}</strong>
              </p>
            )}
          </div>
          <button
            type="button"
            className="scan-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        {loading ? (
          <div className="scan-body">
            <p className="scan-empty">Loading…</p>
          </div>
        ) : error ? (
          <div className="scan-body">
            <p className="scan-error">{error}</p>
          </div>
        ) : (
          <div className={`scan-main ${multi ? "" : "scan-main--single"}`}>
            {multi && (
              <aside className="scan-variants">
                <div className="scan-variants-search">
                  <input
                    type="search"
                    placeholder={`Search ${combos.length} variants…`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="scan-variants-list">
                  {filtered.map((c) => (
                    <button
                      key={c.combinationId}
                      type="button"
                      className={`scan-variant-row ${c.combinationId === selectedId ? "active" : ""}`}
                      onClick={() => setSelectedId(c.combinationId)}
                    >
                      <span className="scan-variant-title">
                        <strong>{variantName(c)}</strong>
                        {c.combinationSku && c.combinationDisplayName && (
                          <small>{c.combinationSku}</small>
                        )}
                      </span>
                      <span className="pill">{c.totalStock}</span>
                    </button>
                  ))}
                  {!filtered.length && (
                    <p className="scan-empty pad">
                      No variants match “{search}”.
                    </p>
                  )}
                </div>
              </aside>
            )}
            <section className="scan-detail">
              {selected && (
                <VariantDetail
                  combo={selected}
                  type={type}
                  materialId={materialId}
                  warehouseList={warehouseList}
                  rackMap={rackMap}
                  fallbackName={combinationName}
                  onDone={handleDone}
                />
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockScanPopup;
